import portalconfig from '../../config';

/*
 * Full - Full access to the client including write access
 * Limited - Limited access to the client: read-only access
 */
export type clientPermissionLevel = 'full' | 'limited';

export interface IUserObj {
  id: number;
  details: {
    id: number;
    email: string;
    nameFirst: string;
    nameLast: string;
    jobTitle?: string | null;
    hasHandbookUpdates: boolean;
    hasTimesheetDue: boolean;
    photoUrl: string;
    isPartTime: boolean;
    isManager: boolean;
    manager?: {
      id: number;
      nameFirst: string;
      nameLast: string;
      email: string;
    } | null;
    tracksPTO: boolean;
    office?: {
      OfficeID: number;
      Name: string;
    } | null;
    department?: {
      DepartmentID: number;
      Name: string;
    } | null;
    permissions: {
      id: number;
      name: string;
    }[];
    accountServicesTeams: {
      id: number;
      permissionType: clientPermissionLevel;
      projectAccountID: number;
    }[];
    featureFlagListV2: {
      name: string;
      active: boolean;
    }[];
  } | null;
  env: string;
  config: typeof portalconfig;
  app: string | null;
  version: string | null;
  isDepartment: (departmentIds: number | number[]) => boolean;
  hasPermissions: (permissionNames: string | string[], allRequired?: boolean) => boolean;
  hasClientPermissions: (
    accountId: number,
    permissionLevels?: clientPermissionLevel | clientPermissionLevel[],
  ) => boolean;
  hasFlags: (flagNames: string | string[], allRequired?: boolean) => boolean;
}

const userObj = (
  usr,
  env: string,
  config: typeof portalconfig,
  name: string | null,
  version: string | null,
): IUserObj => {
  return {
    id: usr.me.id,
    details: {
      ...usr.me,
      featureFlagListV2: usr.featureFlagListV2 || [],
    },
    env: env,
    config: config,
    app: name,
    version: version,
    isDepartment: (departmentIds) => {
      if (Array.isArray(departmentIds)) {
        return departmentIds.some((s) => usr.me.department.DepartmentID === s);
      } else {
        return usr.me.department.DepartmentID === departmentIds;
      }

      return false;
    },
    hasPermissions: (permissionNames, allRequired = false) => {
      if (Array.isArray(permissionNames)) {
        if (permissionNames.length === 0) {
          return true;
        } else if (allRequired) {
          return permissionNames.every((s) => usr.me.permissions.some((ss) => ss.name === s));
        } else {
          return permissionNames.some((s) => usr.me.permissions.some((ss) => ss.name === s));
        }
      } else {
        if (permissionNames === '') {
          return true;
        }
        return usr.me.permissions.some((s) => s.name === permissionNames);
      }

      return false;
    },
    hasClientPermissions: (accountId, permissionLevels) => {
      const permissions = Array.isArray(permissionLevels)
        ? permissionLevels
        : permissionLevels
          ? [permissionLevels]
          : [];

      // admins get full access to all clients
      if (usr.me.permissions.some((s) => s.name === 'crm.admin' || s.name === 'portal.superadmin')) {
        return true;
      }

      // Senior managers get limited access to all clients
      if (usr.me.permissions.some((s) => s.name === 'crm.srmanager')) {
        if (permissions.includes('limited')) {
          return true;
        }
      }

      // we check account id late becasue if the object has no associated account, admins and srmanagers should still have access
      if (!accountId || accountId === 0) {
        return false;
      }

      if (permissions.length === 0) {
        return true;
      }

      const clientPermission = usr.me.accountServicesTeams.find((p) => p.projectAccountID === accountId);

      if (!clientPermission) {
        return false;
      }

      return permissions.some((p) => clientPermission && clientPermission.permissionType === p);
    },
    hasFlags: (flagNames, allRequired = false) => {
      if (Array.isArray(flagNames)) {
        if (allRequired) {
          return flagNames.every((flag) => (usr.featureFlagListV2 || []).some((ff) => ff.name === flag && ff.active));
        } else {
          return flagNames.some((flag) => (usr.featureFlagListV2 || []).some((ff) => ff.name === flag && ff.active));
        }
      } else {
        return (usr.featureFlagListV2 || []).some((flag) => flag.name === flagNames && flag.active);
      }
      return false;
    },
  };
};

export default userObj;
